import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';

/**
 * First-touch acquisition params for one visitor. Every field is optional — the
 * server stamps the capture timestamp and truncates anything over its column
 * caps, so a malformed tracking param can never fail a signup.
 */
export interface Attribution {
  source?:      string;
  medium?:      string;
  campaign?:    string;
  content?:     string;
  term?:        string;
  fbclid?:      string;
  gclid?:       string;
  landingPath?: string;
  referrer?:    string;
}

const STORAGE_KEY = 'emitroll.attribution.v1';

/** Query key -> Attribution field. `utm_content` is the only ad-level key, so
 *  Meta ad URLs must carry utm_content={{ad.id}} (and utm_campaign={{campaign.id}})
 *  or the report can only resolve down to campaign, not to the individual ad. */
const UTM_PARAMS: ReadonlyArray<readonly [string, keyof Attribution]> = [
  ['utm_source',   'source'],
  ['utm_medium',   'medium'],
  ['utm_campaign', 'campaign'],
  ['utm_content',  'content'],
  ['utm_term',     'term']
];

const CLICK_IDS: ReadonlyArray<readonly [string, keyof Attribution]> = [
  ['fbclid', 'fbclid'],
  ['gclid',  'gclid']
];

// Mirror the API's column caps so we never store more than the server keeps.
const MAX_PARAM    = 200;
const MAX_CLICK_ID = 400;
const MAX_URL      = 500;

function clean(value: string | null | undefined, maxLength: number): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed) { return undefined; }
  return trimmed.length <= maxLength ? trimmed : trimmed.slice(0, maxLength);
}

/**
 * Captures how a visitor first reached us and holds it until they register.
 *
 * First touch wins: the params are read once, on the first landing that carries
 * an ad or referral signal, and never overwritten — so a visitor who arrives on
 * an ad, leaves, and comes back directly a day later still attributes to the ad.
 * Persisted to localStorage because the funnel (landing -> /auth/register) can
 * span sessions; `snapshot()` therefore reads storage, never the current URL.
 *
 * First-party only: these are our own query params, stored on our own origin and
 * sent to our own API with a registration the user is actively completing. No
 * third party is involved, so unlike the Meta Pixel / App Insights this is not
 * gated on marketing consent — gating it would leave the acquisition report
 * blind for most signups.
 */
@Injectable({ providedIn: 'root' })
export class AttributionService {
  private readonly doc = inject(DOCUMENT);

  /** In-memory mirror, so the funnel still works when localStorage is blocked.
   *  `undefined` = not read yet; `null` = read, nothing stored (organic). */
  private cached: Attribution | null | undefined;

  /** Idempotent. Call as early as possible in bootstrap, before any route guard
   *  can redirect and drop the query string. */
  capture(): void {
    if (this.snapshot()) { return; }

    const win = this.doc.defaultView;
    if (!win) { return; }

    const captured = this.readFrom(win);
    if (!captured) { return; }   // no ad or referral signal — leave it organic

    this.cached = captured;
    try {
      win.localStorage.setItem(STORAGE_KEY, JSON.stringify(captured));
    } catch { /* storage off — the in-memory copy covers this session */ }
  }

  /** The stored first-touch params, or null when the signup is organic (in which
   *  case callers omit `attribution` from the register body entirely). */
  snapshot(): Attribution | null {
    if (this.cached !== undefined) { return this.cached; }

    this.cached = null;
    const win = this.doc.defaultView;
    if (!win) { return null; }

    try {
      const raw = win.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Attribution | null;
        if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
          this.cached = parsed;
        }
      }
    } catch { /* unreadable or storage off — treat as organic */ }

    return this.cached;
  }

  /** Reads the entry URL + referrer. Returns null unless something real is
   *  there: landingPath/referrer alone would give every direct visit a row. */
  private readFrom(win: Window): Attribution | null {
    const params = new URLSearchParams(win.location.search);
    const out: Attribution = {};
    let hasSignal = false;

    for (const [key, field] of UTM_PARAMS) {
      const value = clean(params.get(key), MAX_PARAM);
      if (value) { out[field] = value; hasSignal = true; }
    }

    for (const [key, field] of CLICK_IDS) {
      const value = clean(params.get(key), MAX_CLICK_ID);
      if (value) { out[field] = value; hasSignal = true; }
    }

    // An external referrer counts on its own (organic search, a shared link).
    // Our own origin doesn't — that's just an internal navigation.
    const referrer = clean(this.doc.referrer, MAX_URL);
    if (referrer && !this.isSameOrigin(referrer, win)) {
      out.referrer = referrer;
      hasSignal = true;
    }

    if (!hasSignal) { return null; }

    out.landingPath = clean(`${win.location.pathname}${win.location.search}`, MAX_URL);
    return out;
  }

  private isSameOrigin(referrer: string, win: Window): boolean {
    try {
      return new URL(referrer).origin === win.location.origin;
    } catch {
      return false;
    }
  }
}
